/* eslint-disable arrow-body-style */
import React, { useEffect, useState } from "react";
import { getIn, useFormikContext } from "formik";
import { toast } from "react-toastify";
import { Dialog } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Tooltip from "../../../components/shared/Tooltip";
import {
  VStack,
  HStack,
  ButtonPrimary1,
  ButtonPrimary,
  ButtonCancelSecondary,
  Error,
  ButtonSecondary,
  Box,
} from "../../../components/utils";
import { Input, Label, TextArea } from "../../../components/shared/InputField";
import { Select } from "../../../components/shared/Select";
import { SwitchButton } from "../../../components/shared/SwitchButton";
import {
  Financier,
  TransactionDetailsData,
  UploadedFile,
  WorkflowStepConfig,
} from "../../../types/Transaction";
import {
  getPresignedUrlForKey,
  validateDocumentTemplate,
  previewDocumentTemplate,
} from "../../../api/Esop";
import { downloadS3File } from "../../../utils/DownloadFile";
import { MultiSelectV2 } from "../../../components/shared/MultiSelectV2";
import { useGetCompanyPaymentProfiles } from "../../../queries/payment";
import { useUserDetailsV2 } from "../../../queries";
import { MonetaryEventState } from "../../../types/OptionBuyback";
import { saveTransactionDetails } from "../../../api/TransactionApisV2";
import TemplateVariablesModal from "./TemplateVariablesModal";

interface ExerciseDetailsProps {
  onStepChange: (e?: number) => void;
  onBackClick: () => void;
}

const defaultRoundingConfiguration = {
  enabled: false,
  roundingStrategy: "ROUND_OFF" as const,
  multiple: 1,
};

const roundingStrategyOptions = ["ROUND_UP", "ROUND_DOWN", "ROUND_OFF"];

function touchAllFields(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(touchAllFields);
  }
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = touchAllFields(obj[key]);
      return acc;
    }, {} as any);
  }
  return true;
}

/** Derive EXERCISE_PAYMENT and TAX_PAYMENT workflow-step config from the
 *  payment-section fields. Returns an updated copy of the steps array. */
function syncPaymentWorkflow(
  steps: WorkflowStepConfig[] | undefined,
  paymentEnabled: boolean,
  taxPaymentEnabled: boolean,
  hissaPayment: boolean,
  extPaymentLink: string | undefined,
  hissaTax: boolean,
  extTaxLink: string | undefined,
  taxMandatory: boolean | undefined,
  paymentIdentifier: string | undefined,
  taxIdentifier: string | undefined
): WorkflowStepConfig[] | undefined {
  if (!steps) return steps;
  return steps.map((s) => {
    if (s.type === "EXERCISE_PAYMENT") {
      if (!paymentEnabled) return { ...s, enabled: false };
      const paymentType = hissaPayment
        ? "HISSA_GENERATED"
        : extPaymentLink
        ? "EXTERNAL"
        : undefined;
      return {
        ...s,
        enabled: true,
        config: {
          ...s.config,
          paymentEnabled: true,
          paymentType: paymentType as any,
          paymentLink: !hissaPayment ? extPaymentLink || "" : undefined,
          paymentIdentifier: hissaPayment ? paymentIdentifier : undefined,
        },
      };
    }
    if (s.type === "TAX_PAYMENT") {
      if (!paymentEnabled || !taxPaymentEnabled)
        return { ...s, enabled: false };
      const taxPaymentType = hissaTax
        ? "HISSA_GENERATED"
        : extTaxLink
        ? "EXTERNAL"
        : undefined;
      return {
        ...s,
        enabled: true,
        config: {
          ...s.config,
          taxPaymentEnabled: true,
          taxPaymentType: taxPaymentType as any,
          taxPaymentLink: !hissaTax ? extTaxLink || "" : undefined,
          taxMandatoryForCompletion: !!taxMandatory,
          taxIdentifier: hissaTax ? taxIdentifier : undefined,
        },
      };
    }
    return s;
  });
}

function generateFinancierId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** Repeatable "financiers" list editor, shared between the Exercise Payment and
 *  Tax Payment sections — each financier has a name and free-text details. */
function FinanciersListEditor({
  financiers,
  fieldPath,
  disabled,
  setFieldValue,
}: {
  financiers: Financier[];
  fieldPath: string;
  disabled: boolean;
  setFieldValue: (field: string, value: any) => void;
}) {
  return (
    <VStack className="gap-3">
      <div className="flex gap-3 overflow-x-auto pb-1">
        {financiers.map((financier, index) => (
          <VStack
            key={financier.id}
            className="gap-2 border rounded-md bg-white p-3 shrink-0 w-80 h-64"
          >
            <HStack className="justify-between items-center">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Financier {index + 1}
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  const updated = [...financiers];
                  updated.splice(index, 1);
                  setFieldValue(fieldPath, updated);
                }}
                className="text-zinc-300 hover:scale-105"
                title="Remove financier"
              >
                <DeleteIcon fontSize="small" />
              </button>
            </HStack>
            <Input
              type="text"
              disabled={disabled}
              className={`${disabled ? "cursor-not-allowed" : ""}`}
              placeholder="Financier name"
              value={financier.name}
              onChange={(e) => {
                if (disabled) return;
                const updated = [...financiers];
                updated[index] = { ...updated[index], name: e.target.value };
                setFieldValue(fieldPath, updated);
              }}
            />
            <TextArea
              className={`flex-1 min-h-0 resize-none ${
                disabled ? "cursor-not-allowed" : ""
              }`}
              disabled={disabled}
              placeholder="Financier details visible to employees..."
              value={financier.details}
              onChange={(e) => {
                if (disabled) return;
                const updated = [...financiers];
                updated[index] = {
                  ...updated[index],
                  details: e.target.value,
                };
                setFieldValue(fieldPath, updated);
              }}
            />
          </VStack>
        ))}
      </div>
      <ButtonSecondary
        className="w-fit"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setFieldValue(fieldPath, [
            ...financiers,
            { id: generateFinancierId(), name: "", details: "" },
          ]);
        }}
      >
        Add Financier
      </ButtonSecondary>
    </VStack>
  );
}

const ExerciseDetails = (props: ExerciseDetailsProps) => {
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    validateForm,
    setTouched,
  } = useFormikContext<TransactionDetailsData>();

  const [inputDisabled, setInputDisabled] = useState(false);

  useEffect(() => {
    if (!values.eventState || values.eventState === MonetaryEventState.DRAFT) {
      setInputDisabled(false);
    } else {
      setInputDisabled(true);
    }
  }, [values.eventState]);

  const {
    sendTransactionEmails,
    transactionPaymentEnabled,
    taxPaymentEnabled = false,
    autoApproveExerciseRequests,
    generateExerciseLetters,
    isDematAccountMandatory,
    allowedDematDepositories,
    hissaGeneratedPaymentEnabled: isPaymentLinkRequired,
    hissaGeneratedTaxPaymentEnabled: isTaxPaymentLinkRequired,
    paymentLink,
    taxPaymentLink,
    exercisePaymentDocumentUploadMandatory,
    taxPaymentDocumentUploadMandatory,
    defaultTemplateLink,
    defaultEmailTemplates,
    taxMandatoryForCompletion,
    paymentData,
    strictlyConsiderCompanyCurrency,
    taxDetailsInfoText,
    workflowSteps,
    allowMultipleRequests,
    exerciseEsopFinancingEnabled,
    exerciseFinanciers,
    taxEsopFinancingEnabled,
    taxFinanciers,
    exerciseRounding: configuredExerciseRounding,
    taxRounding: configuredTaxRounding,
  } = values.monetaryDetails;
  const exerciseRounding =
    configuredExerciseRounding || defaultRoundingConfiguration;
  const taxRounding = configuredTaxRounding || defaultRoundingConfiguration;
  const exerciseRoundingMultipleTouched = getIn(
    touched,
    "monetaryDetails.exerciseRounding.multiple"
  );
  const exerciseRoundingMultipleError = getIn(
    errors,
    "monetaryDetails.exerciseRounding.multiple"
  );
  const taxRoundingMultipleTouched = getIn(
    touched,
    "monetaryDetails.taxRounding.multiple"
  );
  const taxRoundingMultipleError = getIn(
    errors,
    "monetaryDetails.taxRounding.multiple"
  );

  type PaymentMode = "offline" | "link" | "hissa";
  const [exercisePaymentMode, setExercisePaymentMode] = useState<PaymentMode>(
    () => (isPaymentLinkRequired ? "hissa" : paymentLink ? "link" : "offline")
  );
  const [taxPaymentMode, setTaxPaymentMode] = useState<PaymentMode>(() =>
    isTaxPaymentLinkRequired ? "hissa" : taxPaymentLink ? "link" : "offline"
  );

  const { data: paymentProfiles } = useGetCompanyPaymentProfiles();
  const { data: userDetails } = useUserDetailsV2();
  const isInternalUser =
    (userDetails?.emailId || (userDetails as any)?.email_id)?.endsWith(
      "@rulezero.com"
    ) ?? false;
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>("");
  const [validationResults, setValidationResults] = useState<
    { name: string; valid: boolean; reason: string | null }[]
  >([]);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showVariablesModal, setShowVariablesModal] = useState(false);

  const handleSubmit = async () => {
    const validationErrors = await validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setTouched(touchAllFields(values), true);
      return;
    }

    props.onStepChange(2.1);
    if (!values.id) {
      saveTransactionDetails(values);
    }
  };

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    // Validate files and show results popup BEFORE the if block
    const validFileNames = new Set<string>();
    const validFiles: File[] = [];

    if (event.target.files) {
      const files = Array.from(event.target.files);
      const docxFilesToValidate = files.filter(
        (file) =>
          file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );

      if (docxFilesToValidate.length > 0) {
        const results = await Promise.all(
          docxFilesToValidate.map(async (file) => {
            const result = await validateDocumentTemplate(
              file,
              "EXERCISE_LETTER"
            )
              .then((res) => res)
              .catch((err) => {
                const errMsg = err.response.data?.errorRaw;
                return {
                  data: {
                    valid: false,
                    errorRaw: errMsg || "Validation error",
                  },
                };
              });
            const valid = result?.data?.valid ?? false;
            if (valid) validFiles.push(file);
            return {
              valid,
              name: file.name,
              reason: result?.data?.errorRaw || "Validation failed",
            };
          })
        );
        setValidationResults(results);
        if (results.some((res) => !res.valid)) {
          setShowValidationDialog(true);
          toast.error(
            "Please resolve the validation errors before uploading the files."
          );
          return;
        }
      }
    }

    if (validFiles.length > 0) {
      const newFiles = Array.from(validFiles);

      // Filter files to allow only .docx files
      const docxFiles = newFiles.filter(
        (file) =>
          file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );

      if (docxFiles.length === 0) {
        toast.error("Please upload a valid .docx file.");
        return;
      }

      // Convert files to UploadedFile format (only valid files)
      const uploadedFiles = docxFiles.map(async (file) => {
        const base64 = (await fileToBase64(file)).split(",")[1];
        return { name: file.name, type: file.type, size: file.size, base64 };
      });

      Promise.all(uploadedFiles).then((files) => {
        if (files.length === 0) return;
        const updatedTemplates = [
          ...(values.monetaryDetails.customTemplates || []),
          ...files,
        ];
        setFieldValue("monetaryDetails.customTemplates", updatedTemplates);
        const fileInput = document.getElementById(
          "fileInput"
        ) as HTMLInputElement | null;
        if (fileInput) {
          fileInput.value = "";
        }
      });
    }
  }

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  const handleToggleEmail = (template: any) => {
    const updatedTemplates = defaultEmailTemplates?.map((t) =>
      t.templateType === template.templateType
        ? { ...t, sendEmail: !t.sendEmail }
        : t
    );
    setFieldValue("monetaryDetails.defaultEmailTemplates", updatedTemplates);
  };

  const handlePreview = (url: string) => {
    getPresignedUrlForKey(url)
      .then((res) => {
        const s3Url = res.data;
        return fetch(s3Url);
      })
      .then((response) => {
        return response.text();
      })
      .then((htmlContent) => {
        setPreviewContent(htmlContent);
        setShowPreview(true);
      });
  };

  const createDownloadUrl = (base64String: string) => {
    let base64Content = base64String;
    if (base64String.startsWith("data:application/octet-stream;base64,")) {
      base64Content = base64String.split(",")[1];
    }
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/octet-stream" });
    return URL.createObjectURL(blob);
  };

  const handlePreviewCustomTemplate = async (file: UploadedFile) => {
    let base64Content = file.base64;
    if (file.base64.includes(",")) {
      base64Content = file.base64.split(",")[1];
    }
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    const fileObj = new File([blob], file.name, {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    const responseBlob = await previewDocumentTemplate(
      fileObj,
      "EXERCISE_LETTER"
    );
    const url = URL.createObjectURL(responseBlob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `preview_${file.name}`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleDefaultTemplate = (url: string, e: any) => {
    if (url) {
      getPresignedUrlForKey(url).then((res) => {
        downloadS3File(res.data, "DefaultTemplate.docx", false);
      });
    }
    e.preventDefault();
  };

  const [selectedAdminEmails, setSelectedAdminEmails] = useState<boolean>(true);

  return (
    <VStack className="w-full">
      {/* Page Header */}
      <VStack className="justify-between gap-1 pb-4 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg text-gray-900">
          Exercise - Details
        </HStack>
        <HStack className="flex font-medium text-sm text-gray-500">
          Configure payment preferences, employee email triggers, and approval
          flows — everything you need to manage how this exercise event runs.
        </HStack>
      </VStack>

      {/* Section 1: Exercise Confirmation Letters */}
      <VStack className="mx-6 my-3 border rounded-lg bg-white shadow-sm overflow-hidden">
        <HStack className="justify-between items-center px-4 py-3 border-b bg-gray-50">
          <HStack className="items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">
              Exercise Confirmation Letters
            </span>
            {generateExerciseLetters && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700 border border-green-200">
                Active
              </span>
            )}
          </HStack>
          <div className={`${inputDisabled ? "cursor-not-allowed" : ""}`}>
            <SwitchButton
              onClick={() => {
                if (inputDisabled) return;
                setFieldValue(
                  "monetaryDetails.generateExerciseLetters",
                  !generateExerciseLetters
                );
                // Also sync workflow step
                if (workflowSteps) {
                  const updated = (workflowSteps as WorkflowStepConfig[]).map(
                    (s: WorkflowStepConfig) =>
                      s.type === "DOCUMENT_SIGNING"
                        ? { ...s, enabled: !generateExerciseLetters }
                        : s
                  );
                  setFieldValue("monetaryDetails.workflowSteps", updated);
                }
              }}
              className=""
              value={generateExerciseLetters}
              label=""
              disabled={inputDisabled}
            />
          </div>
        </HStack>
        <p className="px-4 py-2 text-xs text-gray-500">
          Show an exercise confirmation letter to employees when they submit a
          request.
        </p>
        {generateExerciseLetters && (
          <VStack className="gap-4 px-4 pb-4 pt-2">
            {!values.monetaryDetails.customTemplates ||
            values.monetaryDetails.customTemplates.length === 0 ? (
              <HStack>
                <a
                  className="flex-1 text-sm font-normal text-blue-600 cursor-pointer hover:underline"
                  href={
                    values.monetaryDetails.defaultTemplateLink
                      ? `/download/${values.monetaryDetails.defaultTemplateLink}`
                      : "#"
                  }
                  onClick={(e) => {
                    const s3Key = values.monetaryDetails.defaultTemplateLink;
                    if (!s3Key) {
                      e.preventDefault();
                      return;
                    }
                    handleDefaultTemplate(s3Key, e);
                  }}
                >
                  Default Template
                </a>
                <div className="flex-1"></div>
              </HStack>
            ) : (
              <HStack>
                <VStack className="gap-2">
                  {values.monetaryDetails.customTemplates?.map(
                    (file, index) => {
                      const downloadUrl = createDownloadUrl(file.base64);
                      return (
                        <HStack
                          key={index}
                          className="items-center justify-between gap-2 text-sm font-normal"
                        >
                          <a
                            href={downloadUrl}
                            download={file.name}
                            className="text-blue-500 hover:underline"
                          >
                            {file.name}
                          </a>
                          {isInternalUser && (
                            <Tooltip
                              text="Download preview docx - renders your template with sample data so you can see how the final document will look"
                              className="!bottom-auto !mb-0 top-full mt-2"
                              _className="w-[400px]"
                            >
                              <div>
                                <button
                                  onClick={() =>
                                    handlePreviewCustomTemplate(file)
                                  }
                                  className="text-blue-500 hover:text-blue-700"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </button>
                              </div>
                            </Tooltip>
                          )}
                          {!inputDisabled && (
                            <button
                              onClick={() => {
                                const updatedTemplates =
                                  values.monetaryDetails.customTemplates?.filter(
                                    (_, i) => i !== index
                                  );
                                setFieldValue(
                                  "monetaryDetails.customTemplates",
                                  updatedTemplates
                                );
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <DeleteIcon fontSize="small" />
                            </button>
                          )}
                        </HStack>
                      );
                    }
                  )}
                </VStack>
                <div className="flex-1"></div>
              </HStack>
            )}
            <HStack className="justify-end gap-2">
              <HStack className="flex-shrink-0 gap-2">
                <ButtonPrimary1
                  className={`px-4 py-2 ${
                    inputDisabled ? "cursor-not-allowed" : ""
                  }`}
                  onClick={() => {
                    const fileInput = document.getElementById(
                      "fileInput"
                    ) as HTMLInputElement | null;
                    if (fileInput) {
                      fileInput.value = "";
                      fileInput.click();
                    }
                  }}
                  disabled={inputDisabled}
                >
                  {!values.monetaryDetails.customTemplates ||
                  values.monetaryDetails.customTemplates.length === 0
                    ? "Add Custom Template"
                    : "Add Another Custom Document"}
                </ButtonPrimary1>
                {isInternalUser && (
                  <ButtonPrimary1
                    className="px-4 py-2"
                    onClick={() => setShowVariablesModal(true)}
                  >
                    <HStack className="items-center gap-1">
                      <span>View Template Variables</span>
                    </HStack>
                  </ButtonPrimary1>
                )}
                {values.monetaryDetails.customTemplates &&
                  values.monetaryDetails.customTemplates.length !== 0 && (
                    <ButtonSecondary
                      className={`px-4 py-2 ${
                        inputDisabled ? "cursor-not-allowed" : ""
                      }`}
                      disabled={inputDisabled}
                      onClick={() => {
                        if (inputDisabled) return;
                        const fileInput = document.getElementById(
                          "fileInput"
                        ) as HTMLInputElement | null;
                        if (fileInput) {
                          fileInput.value = "";
                        }
                        setFieldValue("monetaryDetails.customTemplates", []);
                      }}
                    >
                      Use Default Template
                    </ButtonSecondary>
                  )}
              </HStack>
              <input
                id="fileInput"
                type="file"
                multiple
                className="hidden"
                onChange={handleFileUpload}
              />
            </HStack>
            {/* Signature Flow Toggle */}
            <div className="gap-2">
              <HStack className="justify-between gap-2 ">
                <Label className="mb-0 text-sm font-normal text-gray-700">
                  Enable Signature Flow for Employee
                </Label>
                <div className="justify-end">
                  <SwitchButton
                    onClick={() =>
                      setFieldValue(
                        "monetaryDetails.employeeSignatureRequired",
                        !values.monetaryDetails.employeeSignatureRequired
                      )
                    }
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    onBlur={handleBlur}
                    value={values.monetaryDetails.employeeSignatureRequired}
                    label=""
                  />
                </div>
              </HStack>
              <p className="text-xs text-gray-500 mt-1">
                Employees will need to sign the exercise letter to confirm their
                request.
              </p>
            </div>
            {/* Nested: E-Sign sub-toggle */}
            {values.monetaryDetails.employeeSignatureRequired && (
              <VStack className="pl-4 ml-2 border-l-2 border-gray-200 gap-3">
                <div className="gap-2">
                  <HStack className="justify-between gap-2 ">
                    <Label className="mb-0 text-sm font-normal text-gray-700">
                      Enable E-Sign for Exercise Letter
                    </Label>
                    <div className="justify-end">
                      <SwitchButton
                        onClick={() =>
                          setFieldValue(
                            "monetaryDetails.enableESignForExerciseLetter",
                            !values.monetaryDetails.enableESignForExerciseLetter
                          )
                        }
                        disabled={inputDisabled}
                        className={`${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        onBlur={handleBlur}
                        value={
                          values.monetaryDetails.enableESignForExerciseLetter
                        }
                        label=""
                      />
                    </div>
                  </HStack>
                  <p className="text-xs text-gray-500 mt-1">
                    Use digital e-signing instead of a manual signature flow.
                  </p>
                </div>
                {values.monetaryDetails.enableESignForExerciseLetter && (
                  <VStack className="pl-4 ml-2 border-l-2 border-gray-100 gap-2">
                    <HStack
                      className={`gap-40 ${
                        inputDisabled ? "cursor-not-allowed" : ""
                      }`}
                    >
                      <HStack className="items-center gap-2" radioGroup="">
                        <input
                          type="radio"
                          name="signatureOption"
                          checked={
                            values.monetaryDetails.eSignMethod ===
                            "digio_aadhar"
                          }
                          disabled={inputDisabled}
                          className={`accent-orange-501 outline-hidden mr-1 cursor-pointer ${
                            inputDisabled ? "cursor-not-allowed" : ""
                          }`}
                          onChange={(e) => {
                            setFieldValue(
                              "monetaryDetails.eSignMethod",
                              "digio_aadhar"
                            );
                          }}
                        />
                        <Label className="mt-2">Use Aadhar for Signature</Label>
                      </HStack>
                      <HStack
                        className={`items-center gap-2 ${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="signatureOption"
                          checked={
                            values.monetaryDetails.eSignMethod ===
                            "digio_electronic"
                          }
                          disabled={inputDisabled}
                          className={`accent-orange-501 outline-hidden mr-1 cursor-pointer ${
                            inputDisabled ? "cursor-not-allowed" : ""
                          }`}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            setFieldValue(
                              "monetaryDetails.eSignMethod",
                              "digio_electronic"
                            );
                          }}
                        />
                        <Label className="mt-2">Use Electronic pad</Label>
                      </HStack>
                    </HStack>
                  </VStack>
                )}
              </VStack>
            )}
          </VStack>
        )}
        {!generateExerciseLetters && (
          <p className="px-4 pb-4 text-xs text-gray-400 italic">
            Enable to configure letter templates and signature settings.
          </p>
        )}
      </VStack>

      {/* Section 2: Payment Configuration */}
      <VStack className="mx-6 my-3 border rounded-lg bg-white shadow-sm overflow-hidden">
        <HStack className="justify-between items-center px-4 py-3 border-b bg-gray-50">
          <HStack className="items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">
              Payment Configuration
            </span>
            {transactionPaymentEnabled && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700 border border-green-200">
                Active
              </span>
            )}
          </HStack>
          <div className={`${inputDisabled ? "cursor-not-allowed" : ""}`}>
            <SwitchButton
              onClick={() => {
                const newVal = !transactionPaymentEnabled;
                setFieldValue(
                  "monetaryDetails.transactionPaymentEnabled",
                  newVal
                );
                const updated = syncPaymentWorkflow(
                  workflowSteps,
                  newVal,
                  taxPaymentEnabled,
                  isPaymentLinkRequired,
                  paymentLink,
                  isTaxPaymentLinkRequired,
                  taxPaymentLink,
                  taxMandatoryForCompletion,
                  values.monetaryDetails.paymentData?.paymentIdentifier,
                  values.monetaryDetails.paymentData?.taxIdentifier
                );
                if (updated)
                  setFieldValue("monetaryDetails.workflowSteps", updated);
              }}
              disabled={inputDisabled}
              className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
              value={transactionPaymentEnabled}
              label=""
            />
          </div>
        </HStack>

        {!transactionPaymentEnabled ? (
          <p className="px-4 py-3 text-xs text-gray-400 italic">
            Enable to collect payment from employees during exercise.
          </p>
        ) : (
          <VStack className="px-4 py-4 gap-5">
            {/* Exercise Payment */}
            <VStack className="gap-2">
              <HStack className="justify-between items-center">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Exercise Payment
                </span>
                <div className="inline-flex rounded border divide-x overflow-hidden text-xs">
                  {(
                    [
                      { value: "offline", label: "Offline" },
                      { value: "link", label: "External Link" },
                      { value: "hissa", label: "Via Hissa" },
                    ] as { value: PaymentMode; label: string }[]
                  ).map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      disabled={inputDisabled}
                      onClick={() => {
                        if (inputDisabled) return;
                        setExercisePaymentMode(value);
                        const newHissa = value === "hissa";
                        const newLink =
                          value === "link" ? paymentLink || "" : "";
                        setFieldValue(
                          "monetaryDetails.hissaGeneratedPaymentEnabled",
                          newHissa
                        );
                        if (value !== "link")
                          setFieldValue("monetaryDetails.paymentLink", newLink);
                        const updated = syncPaymentWorkflow(
                          workflowSteps,
                          transactionPaymentEnabled,
                          taxPaymentEnabled,
                          newHissa,
                          newLink,
                          isTaxPaymentLinkRequired,
                          taxPaymentLink,
                          taxMandatoryForCompletion,
                          values.monetaryDetails.paymentData?.paymentIdentifier,
                          values.monetaryDetails.paymentData?.taxIdentifier
                        );
                        if (updated)
                          setFieldValue(
                            "monetaryDetails.workflowSteps",
                            updated
                          );
                      }}
                      className={`px-3 py-1.5 transition-colors ${
                        exercisePaymentMode === value
                          ? "bg-orange-50 text-orange-700 font-semibold"
                          : "bg-white text-gray-500 hover:bg-gray-50"
                      } ${
                        inputDisabled ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </HStack>
              {exercisePaymentMode === "offline" && (
                <p className="text-xs text-gray-400 italic">
                  Employee will upload payment confirmation during exercise.
                </p>
              )}
              {exercisePaymentMode === "link" && (
                <div>
                  <Input
                    type="text"
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    name="monetaryDetails.paymentLink"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      const newLink = e.target.value;
                      const updated = syncPaymentWorkflow(
                        workflowSteps,
                        transactionPaymentEnabled,
                        taxPaymentEnabled,
                        isPaymentLinkRequired,
                        newLink,
                        isTaxPaymentLinkRequired,
                        taxPaymentLink,
                        taxMandatoryForCompletion,
                        values.monetaryDetails.paymentData?.paymentIdentifier,
                        values.monetaryDetails.paymentData?.taxIdentifier
                      );
                      if (updated)
                        setFieldValue("monetaryDetails.workflowSteps", updated);
                    }}
                    placeholder="Enter payment link"
                    value={paymentLink}
                  />
                  {touched.monetaryDetails?.paymentLink &&
                    errors.monetaryDetails?.paymentLink && (
                      <Error text={errors.monetaryDetails.paymentLink} />
                    )}
                </div>
              )}
              {exercisePaymentMode === "hissa" && (
                <Select
                  onChange={(evt) => {
                    if (inputDisabled) return;
                    const newVal = evt.target.value;
                    setFieldValue(
                      "monetaryDetails.paymentData.paymentIdentifier",
                      newVal
                    );
                    const updated = syncPaymentWorkflow(
                      workflowSteps,
                      transactionPaymentEnabled,
                      taxPaymentEnabled,
                      isPaymentLinkRequired,
                      paymentLink,
                      isTaxPaymentLinkRequired,
                      taxPaymentLink,
                      taxMandatoryForCompletion,
                      newVal,
                      values.monetaryDetails.paymentData?.taxIdentifier
                    );
                    if (updated)
                      setFieldValue("monetaryDetails.workflowSteps", updated);
                  }}
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  value={
                    values?.monetaryDetails?.paymentData?.paymentIdentifier
                  }
                  options={paymentProfiles || []}
                  textGetter={(e) => e.name}
                  valueGetter={(e) => e.id}
                />
              )}

              {exercisePaymentMode === "offline" && (
                <VStack className="gap-3 border rounded-md bg-gray-50 p-4 mt-1">
                  <HStack className="w-full justify-between items-center gap-4">
                    <VStack className="flex-1 min-w-0 gap-0.5">
                      <Label className="mb-0 text-sm font-normal text-gray-700">
                        Enable ESOP Financing
                      </Label>
                      <p className="text-xs text-gray-500">
                        Let employees fund this payment through a financier
                        instead of paying themselves.
                      </p>
                    </VStack>
                    <SwitchButton
                      onClick={() => {
                        if (inputDisabled) return;
                        setFieldValue(
                          "monetaryDetails.exerciseEsopFinancingEnabled",
                          !exerciseEsopFinancingEnabled
                        );
                      }}
                      disabled={inputDisabled}
                      className={`shrink-0 ${
                        inputDisabled ? "cursor-not-allowed" : ""
                      }`}
                      value={exerciseEsopFinancingEnabled || false}
                      label=""
                    />
                  </HStack>
                  {!exerciseEsopFinancingEnabled && (
                    <HStack className="w-full justify-between items-center gap-4">
                      <VStack className="flex-1 min-w-0 gap-0.5">
                        <Label className="mb-0 text-sm font-normal text-gray-700">
                          Require Payment Receipt Upload
                        </Label>
                        <p className="text-xs text-gray-500">
                          Employees must attach a receipt when recording an
                          offline exercise payment.
                        </p>
                      </VStack>
                      <SwitchButton
                        onClick={() => {
                          if (inputDisabled) return;
                          const newVal =
                            !exercisePaymentDocumentUploadMandatory;
                          setFieldValue(
                            "monetaryDetails.exercisePaymentDocumentUploadMandatory",
                            newVal
                          );
                          if (workflowSteps) {
                            setFieldValue(
                              "monetaryDetails.workflowSteps",
                              workflowSteps.map((step) =>
                                step.type === "EXERCISE_PAYMENT"
                                  ? {
                                      ...step,
                                      config: {
                                        ...step.config,
                                        documentUploadMandatory: newVal,
                                      },
                                    }
                                  : step
                              )
                            );
                          }
                        }}
                        disabled={inputDisabled}
                        className={`shrink-0 ${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        value={exercisePaymentDocumentUploadMandatory || false}
                        label=""
                      />
                    </HStack>
                  )}
                  {exerciseEsopFinancingEnabled && (
                    <FinanciersListEditor
                      financiers={exerciseFinanciers || []}
                      fieldPath="monetaryDetails.exerciseFinanciers"
                      disabled={inputDisabled}
                      setFieldValue={setFieldValue}
                    />
                  )}
                </VStack>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
                <VStack className="gap-1 border rounded-md bg-white p-4">
                  <Label className="text-sm font-normal">
                    Exercise Payment Note
                    <span className="ml-1 text-xs font-normal text-gray-400">
                      (optional)
                    </span>
                  </Label>
                  <TextArea
                    className={`flex-1 min-h-0 resize-none ${
                      inputDisabled ? "cursor-not-allowed" : ""
                    }`}
                    disabled={inputDisabled}
                    name="exercisePaymentInfoText"
                    onBlur={handleBlur}
                    placeholder="Any additional payment info visible to employees on the exercise payment step..."
                    value={values.monetaryDetails.exercisePaymentInfoText || ""}
                    onChange={(evt) => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.exercisePaymentInfoText",
                        evt.target.value
                      );
                    }}
                  />
                </VStack>

                <VStack className="gap-1 border rounded-md bg-white p-4">
                  <Label className="text-sm font-normal">
                    Approval Pending Note
                    <span className="ml-1 text-xs font-normal text-gray-400">
                      (optional)
                    </span>
                  </Label>
                  <p className="text-xs text-gray-500">
                    Shown to employees while an offline/external link exercise
                    payment is awaiting admin verification.
                  </p>
                  <TextArea
                    className={`min-h-24 ${
                      inputDisabled ? "cursor-not-allowed" : ""
                    }`}
                    disabled={inputDisabled}
                    name="exercisePaymentApprovalNoteText"
                    onBlur={handleBlur}
                    placeholder="E.g. Payments are typically verified within 1-2 business days..."
                    value={
                      values.monetaryDetails.exercisePaymentApprovalNoteText ||
                      ""
                    }
                    onChange={(evt) => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.exercisePaymentApprovalNoteText",
                        evt.target.value
                      );
                    }}
                  />
                </VStack>
              </div>
            </VStack>

            <div className="border-t" />

            <HStack className="justify-between items-center gap-4">
              <VStack className="gap-0.5">
                <Label className="mb-0 text-sm font-normal text-gray-700">
                  Enable Tax Payment
                </Label>
                <p className="text-xs text-gray-500">
                  Calculate and collect tax during exercise.
                </p>
              </VStack>
              <SwitchButton
                onClick={() => {
                  if (inputDisabled) return;
                  const newVal = !taxPaymentEnabled;
                  setFieldValue("monetaryDetails.taxPaymentEnabled", newVal);
                  const updated = syncPaymentWorkflow(
                    workflowSteps,
                    transactionPaymentEnabled,
                    newVal,
                    isPaymentLinkRequired,
                    paymentLink,
                    isTaxPaymentLinkRequired,
                    taxPaymentLink,
                    taxMandatoryForCompletion,
                    values.monetaryDetails.paymentData?.paymentIdentifier,
                    values.monetaryDetails.paymentData?.taxIdentifier
                  );
                  if (updated)
                    setFieldValue("monetaryDetails.workflowSteps", updated);
                }}
                disabled={inputDisabled}
                value={taxPaymentEnabled}
                label=""
              />
            </HStack>

            {taxPaymentEnabled ? (
              <>
                <VStack className="gap-2">
                  <HStack className="justify-between items-center">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Tax Payment
                    </span>
                    <div className="inline-flex rounded border divide-x overflow-hidden text-xs">
                      {(
                        [
                          { value: "offline", label: "Offline" },
                          { value: "link", label: "External Link" },
                          { value: "hissa", label: "Via Hissa" },
                        ] as { value: PaymentMode; label: string }[]
                      ).map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          disabled={inputDisabled}
                          onClick={() => {
                            if (inputDisabled) return;
                            setTaxPaymentMode(value);
                            const newHissa = value === "hissa";
                            const newLink =
                              value === "link" ? taxPaymentLink || "" : "";
                            setFieldValue(
                              "monetaryDetails.hissaGeneratedTaxPaymentEnabled",
                              newHissa
                            );
                            if (value !== "link")
                              setFieldValue(
                                "monetaryDetails.taxPaymentLink",
                                newLink
                              );
                            const updated = syncPaymentWorkflow(
                              workflowSteps,
                              transactionPaymentEnabled,
                              taxPaymentEnabled,
                              isPaymentLinkRequired,
                              paymentLink,
                              newHissa,
                              newLink,
                              taxMandatoryForCompletion,
                              values.monetaryDetails.paymentData
                                ?.paymentIdentifier,
                              values.monetaryDetails.paymentData?.taxIdentifier
                            );
                            if (updated)
                              setFieldValue(
                                "monetaryDetails.workflowSteps",
                                updated
                              );
                          }}
                          className={`px-3 py-1.5 transition-colors ${
                            taxPaymentMode === value
                              ? "bg-orange-50 text-orange-700 font-semibold"
                              : "bg-white text-gray-500 hover:bg-gray-50"
                          } ${
                            inputDisabled
                              ? "cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </HStack>
                  {taxPaymentMode === "offline" && (
                    <p className="text-xs text-gray-400 italic">
                      Employee will upload tax payment confirmation during
                      exercise.
                    </p>
                  )}
                  {taxPaymentMode === "link" && (
                    <div>
                      <Input
                        type="text"
                        disabled={inputDisabled}
                        className={`${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        name="monetaryDetails.taxPaymentLink"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          handleChange(e);
                          const newLink = e.target.value;
                          const updated = syncPaymentWorkflow(
                            workflowSteps,
                            transactionPaymentEnabled,
                            taxPaymentEnabled,
                            isPaymentLinkRequired,
                            paymentLink,
                            isTaxPaymentLinkRequired,
                            newLink,
                            taxMandatoryForCompletion,
                            values.monetaryDetails.paymentData
                              ?.paymentIdentifier,
                            values.monetaryDetails.paymentData?.taxIdentifier
                          );
                          if (updated)
                            setFieldValue(
                              "monetaryDetails.workflowSteps",
                              updated
                            );
                        }}
                        placeholder="Enter tax payment link"
                        value={taxPaymentLink}
                      />
                      {touched.monetaryDetails?.taxPaymentLink &&
                        errors.monetaryDetails?.taxPaymentLink && (
                          <Error text={errors.monetaryDetails.taxPaymentLink} />
                        )}
                    </div>
                  )}
                  {taxPaymentMode === "hissa" && (
                    <Select
                      onChange={(evt) => {
                        if (inputDisabled) return;
                        const newVal = evt.target.value;
                        setFieldValue(
                          "monetaryDetails.paymentData.taxIdentifier",
                          newVal
                        );
                        const updated = syncPaymentWorkflow(
                          workflowSteps,
                          transactionPaymentEnabled,
                          taxPaymentEnabled,
                          isPaymentLinkRequired,
                          paymentLink,
                          isTaxPaymentLinkRequired,
                          taxPaymentLink,
                          taxMandatoryForCompletion,
                          values.monetaryDetails.paymentData?.paymentIdentifier,
                          newVal
                        );
                        if (updated)
                          setFieldValue(
                            "monetaryDetails.workflowSteps",
                            updated
                          );
                      }}
                      disabled={inputDisabled}
                      className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                      value={
                        values?.monetaryDetails?.paymentData?.taxIdentifier
                      }
                      options={paymentProfiles || []}
                      textGetter={(e) => e.name}
                      valueGetter={(e) => e.id}
                    />
                  )}

                  {taxPaymentMode === "offline" && (
                    <VStack className="gap-3 border rounded-md bg-gray-50 p-4 mt-1">
                      <HStack className="w-full justify-between items-center gap-4">
                        <VStack className="flex-1 min-w-0 gap-0.5">
                          <Label className="mb-0 text-sm font-normal text-gray-700">
                            Enable ESOP Financing
                          </Label>
                          <p className="text-xs text-gray-500">
                            Let employees fund this payment through a financier
                            instead of paying themselves.
                          </p>
                        </VStack>
                        <SwitchButton
                          onClick={() => {
                            if (inputDisabled) return;
                            setFieldValue(
                              "monetaryDetails.taxEsopFinancingEnabled",
                              !taxEsopFinancingEnabled
                            );
                          }}
                          disabled={inputDisabled}
                          className={`shrink-0 ${
                            inputDisabled ? "cursor-not-allowed" : ""
                          }`}
                          value={taxEsopFinancingEnabled || false}
                          label=""
                        />
                      </HStack>
                      {!taxEsopFinancingEnabled && (
                        <HStack className="w-full justify-between items-center gap-4">
                          <VStack className="flex-1 min-w-0 gap-0.5">
                            <Label className="mb-0 text-sm font-normal text-gray-700">
                              Require Tax Payment Receipt Upload
                            </Label>
                            <p className="text-xs text-gray-500">
                              Employees must attach a receipt when recording an
                              offline tax payment.
                            </p>
                          </VStack>
                          <SwitchButton
                            onClick={() => {
                              if (inputDisabled) return;
                              const newVal = !taxPaymentDocumentUploadMandatory;
                              setFieldValue(
                                "monetaryDetails.taxPaymentDocumentUploadMandatory",
                                newVal
                              );
                              if (workflowSteps) {
                                setFieldValue(
                                  "monetaryDetails.workflowSteps",
                                  workflowSteps.map((step) =>
                                    step.type === "TAX_PAYMENT"
                                      ? {
                                          ...step,
                                          config: {
                                            ...step.config,
                                            documentUploadMandatory: newVal,
                                          },
                                        }
                                      : step
                                  )
                                );
                              }
                            }}
                            disabled={inputDisabled}
                            className={`shrink-0 ${
                              inputDisabled ? "cursor-not-allowed" : ""
                            }`}
                            value={taxPaymentDocumentUploadMandatory || false}
                            label=""
                          />
                        </HStack>
                      )}
                      {taxEsopFinancingEnabled && (
                        <FinanciersListEditor
                          financiers={taxFinanciers || []}
                          fieldPath="monetaryDetails.taxFinanciers"
                          disabled={inputDisabled}
                          setFieldValue={setFieldValue}
                        />
                      )}
                    </VStack>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
                    <VStack className="gap-1 border rounded-md bg-white p-4">
                      <Label className="text-sm font-normal">
                        Tax Payment Note
                        <span className="ml-1 text-xs font-normal text-gray-400">
                          (optional)
                        </span>
                      </Label>
                      <TextArea
                        className={`flex-1 min-h-0 resize-none ${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        disabled={inputDisabled}
                        name="taxDetailsInfoText"
                        onBlur={handleBlur}
                        placeholder="Any additional payment or tax info visible to employees on the tax payment step..."
                        value={values.monetaryDetails.taxDetailsInfoText || ""}
                        onChange={(evt) => {
                          if (inputDisabled) return;
                          setFieldValue(
                            "monetaryDetails.taxDetailsInfoText",
                            evt.target.value
                          );
                        }}
                      />
                    </VStack>

                    <VStack className="gap-1 border rounded-md bg-white p-4">
                      <Label className="text-sm font-normal">
                        Approval Pending Note
                        <span className="ml-1 text-xs font-normal text-gray-400">
                          (optional)
                        </span>
                      </Label>
                      <p className="text-xs text-gray-500">
                        Shown to employees while an offline/external link tax
                        payment is awaiting admin verification.
                      </p>
                      <TextArea
                        className={`min-h-24 ${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        disabled={inputDisabled}
                        name="taxPaymentApprovalNoteText"
                        onBlur={handleBlur}
                        placeholder="E.g. Payments are typically verified within 1-2 business days..."
                        value={
                          values.monetaryDetails.taxPaymentApprovalNoteText ||
                          ""
                        }
                        onChange={(evt) => {
                          if (inputDisabled) return;
                          setFieldValue(
                            "monetaryDetails.taxPaymentApprovalNoteText",
                            evt.target.value
                          );
                        }}
                      />
                    </VStack>
                  </div>
                </VStack>

                <div className="border-t" />

                {/* Tax mandatory */}
                <HStack className="justify-between items-center gap-4">
                  <VStack className="gap-0.5">
                    <Label className="mb-0 text-sm font-normal text-gray-700">
                      Require Tax Payment Before Shares Are Issued
                    </Label>
                    <p className="text-xs text-gray-500">
                      Employees must pay tax before shares can be allocated.
                    </p>
                  </VStack>
                  <SwitchButton
                    onClick={() => {
                      if (inputDisabled) return;
                      const newVal = !taxMandatoryForCompletion;
                      setFieldValue(
                        "monetaryDetails.taxMandatoryForCompletion",
                        newVal
                      );
                      const updated = syncPaymentWorkflow(
                        workflowSteps,
                        transactionPaymentEnabled,
                        taxPaymentEnabled,
                        isPaymentLinkRequired,
                        paymentLink,
                        isTaxPaymentLinkRequired,
                        taxPaymentLink,
                        newVal,
                        values.monetaryDetails.paymentData?.paymentIdentifier,
                        values.monetaryDetails.paymentData?.taxIdentifier
                      );
                      if (updated)
                        setFieldValue("monetaryDetails.workflowSteps", updated);
                    }}
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    value={taxMandatoryForCompletion || false}
                    label=""
                  />
                </HStack>
              </>
            ) : null}
          </VStack>
        )}
      </VStack>

      {/* Section 3: Email Notifications */}
      <VStack className="mx-6 my-3 border rounded-lg bg-white shadow-sm overflow-hidden">
        <HStack className="justify-between items-center px-4 py-3 border-b bg-gray-50">
          <HStack className="items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">
              Email Notifications
            </span>
            {sendTransactionEmails && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700 border border-green-200">
                Active
              </span>
            )}
          </HStack>
          <div className={`${inputDisabled ? "cursor-not-allowed" : ""}`}>
            <SwitchButton
              onClick={() => {
                if (inputDisabled) return;
                setFieldValue(
                  "monetaryDetails.sendTransactionEmails",
                  !sendTransactionEmails
                );
              }}
              disabled={inputDisabled}
              className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
              value={sendTransactionEmails}
              label=""
            />
          </div>
        </HStack>
        <p className="px-4 py-2 text-xs text-gray-500">
          Send email notifications to admins and employees for exercise workflow
          events. Make sure to enable Hissa email toggle from settings.
        </p>
        {sendTransactionEmails && (
          <VStack className="mx-4 mb-4 border rounded-md bg-gray-50 overflow-hidden">
            <HStack className="gap-6 px-4 border-b bg-white">
              <Box
                onClick={() => {
                  setSelectedAdminEmails(!selectedAdminEmails);
                }}
                className={`text-xs3 font-semibold cursor-pointer py-2 ${
                  selectedAdminEmails
                    ? "border-b-2 border-solid border-red-500 text-red-500"
                    : "text-gray-201 "
                } `}
              >
                <p>Emails to Admin</p>
              </Box>
              <Box
                onClick={() => {
                  setSelectedAdminEmails(!selectedAdminEmails);
                }}
                className={`text-xs3 font-semibold cursor-pointer py-2 ${
                  !selectedAdminEmails
                    ? "border-b-2 border-solid border-red-500 text-red-500"
                    : "text-gray-201 "
                } `}
              >
                <p>Emails to Employee</p>
              </Box>
            </HStack>
            {selectedAdminEmails && (
              <VStack className="px-4 py-2 ">
                <HStack>
                  <div className="flex-1 pt-4">
                    <HStack className="justify-between gap-1 ">
                      <Label className="mb-0 text-sm font-normal text-gray-700">
                        Send Emails to Admins
                      </Label>
                      <div className="justify-end">
                        <SwitchButton
                          onClick={() => {
                            if (inputDisabled) return;
                            setFieldValue(
                              "monetaryDetails.sendTransactionEmailsToAdmin",
                              !values.monetaryDetails
                                .sendTransactionEmailsToAdmin
                            );
                          }}
                          disabled={inputDisabled}
                          className={`${
                            inputDisabled ? "cursor-not-allowed" : ""
                          }`}
                          value={
                            values.monetaryDetails.sendTransactionEmailsToAdmin
                          }
                          label=""
                        />
                      </div>
                    </HStack>
                    <p className="text-xs text-gray-500 mt-1">
                      Notify admins at each stage of the exercise workflow.
                    </p>
                  </div>
                </HStack>
                {values.monetaryDetails.sendTransactionEmailsToAdmin &&
                  values.monetaryDetails.defaultEmailTemplates
                    ?.filter((template) => template.to === "Admin")
                    .map((template, index) => {
                      return (
                        <VStack key={index} className="py-2 pl-4">
                          <div className="flex-1 pt-4 mb-0">
                            <HStack className="justify-between gap-1">
                              <VStack className="gap-1">
                                <Label className="mb-0 text-sm font-normal cursor-pointer hover:underline">
                                  {template.templateName}
                                </Label>
                                <p className="text-xs text-gray-500">
                                  {template.description ||
                                    "No description available"}
                                </p>
                              </VStack>
                              <HStack className="items-center justify-end gap-6">
                                <HStack>
                                  <ButtonPrimary1
                                    disabled={!template.templateLink}
                                    onClick={() =>
                                      handlePreview(template.templateLink)
                                    }
                                  >
                                    Preview
                                  </ButtonPrimary1>
                                </HStack>
                                <HStack>
                                  <SwitchButton
                                    onClick={() => {
                                      if (inputDisabled) return;
                                      handleToggleEmail(template);
                                    }}
                                    disabled={inputDisabled}
                                    className={
                                      inputDisabled ? "cursor-not-allowed" : ""
                                    }
                                    value={template.sendEmail}
                                    label=""
                                  />
                                </HStack>
                              </HStack>
                            </HStack>
                          </div>
                        </VStack>
                      );
                    })}
              </VStack>
            )}
            {!selectedAdminEmails && (
              <VStack className="px-4 py-2 ">
                <HStack>
                  <div className="flex-1 pt-4">
                    <HStack className="justify-between gap-2 ">
                      <Label className="mb-0 text-sm font-normal text-gray-700">
                        Send Emails to Employees
                      </Label>
                      <div className="justify-end">
                        <SwitchButton
                          onClick={() => {
                            if (inputDisabled) return;
                            setFieldValue(
                              "monetaryDetails.sendTransactionEmailsToEmployee",
                              !values.monetaryDetails
                                .sendTransactionEmailsToEmployee
                            );
                          }}
                          disabled={inputDisabled}
                          className={`${
                            inputDisabled ? "cursor-not-allowed" : ""
                          }`}
                          value={
                            values.monetaryDetails
                              .sendTransactionEmailsToEmployee
                          }
                          label=""
                        />
                      </div>
                    </HStack>
                    <p className="text-xs text-gray-500 mt-1">
                      Notify employees at each stage of the exercise workflow.
                    </p>
                  </div>
                </HStack>
                {values.monetaryDetails.sendTransactionEmailsToEmployee &&
                  values.monetaryDetails.defaultEmailTemplates
                    ?.filter((template) => template.to === "Employee")
                    .map((template, index) => {
                      return (
                        <VStack key={index} className="py-2 pl-4">
                          <div className="flex-1 pt-4 mb-0">
                            <HStack className="justify-between gap-1">
                              <VStack className="gap-1">
                                <Label className="mb-0 text-sm font-normal cursor-pointer hover:underline">
                                  {template.templateName}
                                </Label>
                                <p className="text-xs text-gray-500">
                                  {template.description ||
                                    "No description available"}
                                </p>
                              </VStack>
                              <HStack className="items-center justify-end gap-6">
                                <HStack>
                                  <ButtonPrimary1
                                    disabled={!template.templateLink}
                                    onClick={() =>
                                      handlePreview(template.templateLink)
                                    }
                                  >
                                    Preview
                                  </ButtonPrimary1>
                                </HStack>
                                <HStack>
                                  <SwitchButton
                                    onClick={() => {
                                      if (inputDisabled) return;
                                      handleToggleEmail(template);
                                    }}
                                    disabled={inputDisabled}
                                    className={`${
                                      inputDisabled ? "cursor-not-allowed" : ""
                                    }`}
                                    value={template.sendEmail}
                                    label=""
                                  />
                                </HStack>
                              </HStack>
                            </HStack>
                          </div>
                        </VStack>
                      );
                    })}
              </VStack>
            )}
          </VStack>
        )}
        {!sendTransactionEmails && (
          <p className="px-4 pb-4 text-xs text-gray-400 italic">
            Enable to configure email triggers for admins and employees.
          </p>
        )}
      </VStack>

      {/* Section 4: Transaction Settings */}
      <VStack className="mx-6 my-3 border rounded-lg bg-white shadow-sm overflow-hidden">
        <HStack className="items-center gap-2 px-4 py-3 border-b bg-gray-50">
          <span className="text-sm font-semibold text-gray-800">
            Transaction Settings
          </span>
        </HStack>
        <VStack className="px-4 py-2 gap-0">
          <VStack className="gap-1 py-3 border-b">
            <HStack className="justify-between gap-2 ">
              <Label className="mb-0 text-sm font-normal text-gray-700">
                Auto Approve Exercise Requests
              </Label>
              <div className="justify-end">
                <SwitchButton
                  onClick={() => {
                    if (inputDisabled) return;
                    setFieldValue(
                      "monetaryDetails.autoApproveExerciseRequests",
                      !autoApproveExerciseRequests
                    );
                  }}
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  value={autoApproveExerciseRequests || false}
                  label=""
                />
              </div>
            </HStack>
            <p className="text-xs text-gray-500">
              Automatically approve all incoming exercise requests. Disable for
              a manual review-based approval flow. Requires payment link to be
              enabled.
            </p>
          </VStack>
          <VStack className="gap-1 py-3 border-b">
            <HStack className="justify-between gap-2">
              <Label className="mb-0 text-sm font-normal text-gray-700">
                Round Exercise Price
              </Label>
              <div className="justify-end">
                <SwitchButton
                  onClick={() => {
                    if (inputDisabled) return;
                    setFieldValue("monetaryDetails.exerciseRounding", {
                      ...exerciseRounding,
                      enabled: !exerciseRounding.enabled,
                    });
                  }}
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  value={exerciseRounding.enabled}
                  label=""
                />
              </div>
            </HStack>
            <p className="text-xs text-gray-500">
              Round the final exercise price displayed to employees. This is
              disabled by default.
            </p>
            {exerciseRounding.enabled && (
              <div className="grid grid-cols-1 gap-4 pt-3 md:grid-cols-2">
                <div>
                  <Label className="mb-1 text-xs font-normal text-gray-600">
                    Strategy
                  </Label>
                  <Select
                    disabled={inputDisabled}
                    options={roundingStrategyOptions}
                    value={exerciseRounding.roundingStrategy}
                    onChange={(event) =>
                      setFieldValue(
                        "monetaryDetails.exerciseRounding.roundingStrategy",
                        event.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <Label className="mb-1 text-xs font-normal text-gray-600">
                    Nearest Multiple
                  </Label>
                  <Input
                    type="number"
                    min="0.000001"
                    step="any"
                    disabled={inputDisabled}
                    value={exerciseRounding.multiple ?? ""}
                    onChange={(event) => {
                      const { value } = event.target;
                      setFieldValue(
                        "monetaryDetails.exerciseRounding.multiple",
                        value === "" ? "" : Number(value)
                      );
                    }}
                  />
                  {exerciseRoundingMultipleTouched &&
                    exerciseRoundingMultipleError && (
                      <Error text={exerciseRoundingMultipleError} />
                    )}
                </div>
              </div>
            )}
          </VStack>
          <VStack className="gap-1 py-3 border-b">
            <HStack className="justify-between gap-2">
              <Label className="mb-0 text-sm font-normal text-gray-700">
                Round Tax Price
              </Label>
              <div className="justify-end">
                <SwitchButton
                  onClick={() => {
                    if (inputDisabled) return;
                    setFieldValue("monetaryDetails.taxRounding", {
                      ...taxRounding,
                      enabled: !taxRounding.enabled,
                    });
                  }}
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  value={taxRounding.enabled}
                  label=""
                />
              </div>
            </HStack>
            <p className="text-xs text-gray-500">
              Round the final tax price displayed to employees. This is disabled
              by default.
            </p>
            {taxRounding.enabled && (
              <div className="grid grid-cols-1 gap-4 pt-3 md:grid-cols-2">
                <div>
                  <Label className="mb-1 text-xs font-normal text-gray-600">
                    Strategy
                  </Label>
                  <Select
                    disabled={inputDisabled}
                    options={roundingStrategyOptions}
                    value={taxRounding.roundingStrategy}
                    onChange={(event) =>
                      setFieldValue(
                        "monetaryDetails.taxRounding.roundingStrategy",
                        event.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <Label className="mb-1 text-xs font-normal text-gray-600">
                    Nearest Multiple
                  </Label>
                  <Input
                    type="number"
                    min="0.000001"
                    step="any"
                    disabled={inputDisabled}
                    value={taxRounding.multiple ?? ""}
                    onChange={(event) => {
                      const { value } = event.target;
                      setFieldValue(
                        "monetaryDetails.taxRounding.multiple",
                        value === "" ? "" : Number(value)
                      );
                    }}
                  />
                  {taxRoundingMultipleTouched && taxRoundingMultipleError && (
                    <Error text={taxRoundingMultipleError} />
                  )}
                </div>
              </div>
            )}
          </VStack>
          <VStack className="gap-1 py-3 border-b">
            <HStack className="justify-between gap-2 ">
              <Label className="mb-0 text-sm font-normal text-gray-700">
                Require Demat Account Details
              </Label>
              <div className="justify-end">
                <SwitchButton
                  onClick={() => {
                    if (inputDisabled) return;
                    setFieldValue(
                      "monetaryDetails.isDematAccountMandatory",
                      !isDematAccountMandatory
                    );
                  }}
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  value={isDematAccountMandatory || false}
                  label=""
                />
              </div>
            </HStack>
            <p className="text-xs text-gray-500">
              Employees must provide demat account details before completing the
              exercise process.
            </p>
            {isDematAccountMandatory && (
              <HStack className="gap-8 mt-2">
                <div className="flex-1">
                  <Label className="mb-0 text-sm font-normal">
                    Allowed Depositories
                  </Label>
                  <MultiSelectV2
                    multiple={true}
                    placeholder=""
                    optionValues={allowedDematDepositories}
                    _className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    handleSelection={(vals) => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.allowedDematDepositories",
                        vals
                      );
                    }}
                    options={[{ value: "CDSL" }, { value: "NSDL" }]}
                  />
                  {touched.monetaryDetails?.allowedDematDepositories &&
                    errors.monetaryDetails?.allowedDematDepositories && (
                      <Error
                        text={
                          errors.monetaryDetails
                            .allowedDematDepositories as string
                        }
                      ></Error>
                    )}
                </div>
                <div className="flex-1"></div>
              </HStack>
            )}
          </VStack>
          <VStack className="gap-1 py-3 border-b">
            <HStack className="justify-between gap-2 ">
              <Label className="mb-0 text-sm font-normal text-gray-700">
                Use Company Currency for Exercise Letters
              </Label>
              <div className="justify-end">
                <SwitchButton
                  onClick={() => {
                    if (inputDisabled) return;
                    setFieldValue(
                      "monetaryDetails.strictlyConsiderCompanyCurrency",
                      !strictlyConsiderCompanyCurrency
                    );
                  }}
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  value={strictlyConsiderCompanyCurrency ?? true}
                  label=""
                />
              </div>
            </HStack>
            <p className="text-xs text-gray-500">
              {`Generated exercise letters use the company's base currency when enabled. When disabled, letters use each employee's preferred currency.`}
            </p>
          </VStack>
          <VStack className="gap-1 py-3 border-b">
            <HStack className="justify-between gap-2 ">
              <Label className="mb-0 text-sm font-normal text-gray-700">
                Allow Multiple Requests per Employee
              </Label>
              <div className="justify-end">
                <SwitchButton
                  onClick={() => {
                    if (inputDisabled) return;
                    setFieldValue(
                      "monetaryDetails.allowMultipleRequests",
                      !allowMultipleRequests
                    );
                  }}
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  value={allowMultipleRequests || false}
                  label=""
                />
              </div>
            </HStack>
            <p className="text-xs text-gray-500">
              Allow employees to raise multiple exercise requests in the same
              event.
            </p>
          </VStack>
          <VStack className="gap-1 py-3">
            <Label className="text-sm font-normal">
              Additional Info for Employees
            </Label>
            <p className="text-xs text-gray-500 pb-2">
              Any additional information about the exercise flow that will be
              visible to employees in the final step.
            </p>
            <TextArea
              className={`min-h-32 ${
                inputDisabled ? "cursor-not-allowed" : ""
              }`}
              disabled={inputDisabled}
              name="additionalPaymentInfoText"
              onBlur={handleBlur}
              placeholder="Write here..."
              value={values.monetaryDetails.additionalPaymentInfoText || ""}
              onChange={(evt) => {
                if (inputDisabled) return;
                setFieldValue(
                  "monetaryDetails.additionalPaymentInfoText",
                  evt.target.value
                );
              }}
            />
          </VStack>
        </VStack>
      </VStack>

      {/* Modals */}
      <TemplateVariablesModal
        open={showVariablesModal}
        onClose={() => setShowVariablesModal(false)}
        templateType="EXERCISE_LETTER"
      />
      <Dialog
        maxWidth="sm"
        fullWidth
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
      >
        <VStack className="bg-white rounded-lg overflow-hidden">
          <HStack className="items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
            <HStack className="items-center gap-2">
              <p className="text-base font-semibold text-gray-800">
                File Validation Results
              </p>
            </HStack>
            <button
              onClick={() => setShowValidationDialog(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            ></button>
          </HStack>
          <VStack className="gap-2 px-6 pt-4 pb-6">
            {validationResults.map((result, index) => (
              <HStack
                key={index}
                className={`items-start gap-3 p-3 rounded-md border ${
                  result.valid
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <VStack className="gap-0.5">
                  <p className="text-sm font-medium text-gray-800">
                    {result.name}
                  </p>
                  {!result.valid && (
                    <p className="text-xs text-red-600">{result.reason}</p>
                  )}
                  {result.valid && (
                    <p className="text-xs text-green-600">Valid template</p>
                  )}
                </VStack>
              </HStack>
            ))}
          </VStack>
          <div className="flex justify-end px-6 pb-6">
            <ButtonPrimary onClick={() => setShowValidationDialog(false)}>
              Close
            </ButtonPrimary>
          </div>
        </VStack>
      </Dialog>
      <Dialog
        maxWidth="xl"
        open={showPreview}
        onClose={() => setShowPreview(false)}
      >
        <div className="mx-auto bg-white rounded-lg min-w-min min-h-min">
          <div className="font-semibold border-b p-7">
            <div className="flex items-center justify-between">
              <VStack className="gap-2 text-lg2 text-black-501">
                <p>Preview</p>
                <p className="text-sm4 text-gray-401">Email Template Preview</p>
              </VStack>
              <span
                className="cursor-pointer"
                onClick={() => setShowPreview(false)}
              >
                X
              </span>
            </div>
            <VStack className="w-full gap-4 p-7">
              <div dangerouslySetInnerHTML={{ __html: previewContent }} />
            </VStack>
          </div>
        </div>
      </Dialog>

      {/* Footer */}
      <HStack className="justify-between px-6 pt-4 pb-8">
        <ButtonPrimary1
          onClick={() => {
            props.onBackClick();
          }}
        >
          Back
        </ButtonPrimary1>
        <ButtonPrimary
          onClick={() => {
            handleSubmit();
          }}
        >
          Next
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
};

export default ExerciseDetails;
